// ───────────────────────────────────────────────
// 📦 ImageFilePage.jsx
// - 파일 상세 페이지 (초음파 / 열화상 / 자외선 / 보고서)
// - 업로드 파일별 상세정보, 편집, 이미지 저장 관리
// ───────────────────────────────────────────────

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryParams } from "lib/hooks/useQureyParmas";
import { useAppStore } from "lib/store/useAppStore";

// 🔗 API
import { getFile } from "lib/api/getFile";
import { postUpdateThermalInfo } from "lib/api/postUpdateThermalInfo";

// 🧩 Components
import TabMenu from "components/project/TabMenu";
import EditorBtnBox from "components/project/EditorBtnBox";
import GoBackButton from "components/project/GoBackButton";
// import UltrasonicPanel from "pages/files/image/ultrasonic/UltrasonicPanel";
// import ThermalPanel from "pages/files/image/thermal/ThermalPanel";
// import UltravioletPanel from "pages/files/image/ultraviolet/UltravioletPanel";
import ThreePhasePanel from "pages/files/image/threePhase/ThreePhasePanel";
import FusionPanel, {
  FusionDownloadModal,
  downloadImageFromSource,
  getDownloadableImageItems,
  inferFileExtension,
  sanitizeFileName,
} from "pages/files/image/fusion/FusionPanel";
import ImageReportPanel from "pages/files/image/report/ImageReportPanel";
import Spinner from "components/Spinner";
// import ImageCalibrationPanel from "pages/files/image/calibration/ImageCalibrationPanel";
import DownloadUdFile from "components/project/DownloadUdFile";
import { getFileSortPref } from "lib/utils/fileSortpref";
import { parseFileDetailFiltersFromSearchParams } from "lib/utils/fileDetailFilters";
import { resolveFileDetailTab } from "lib/utils/fileDetailTabs";
import PrevNextNavToFIleIndex from "components/project/PrevNextNavToFIleIndex";
import FileTreePath from "components/project/FileTreePath";
import HelpTip from "components/HelpTip";
import FileHistoryPanel from "./history/FileHistoryPanel";
import "css/fileTabHeaders.css";

const TAB_KEYS = {
  // ULTRASOUND: "ultrasound",
  // THERMAL: "thermal",
  // ULTRAVIOLET: "ultraviolet",
  FUSION: "fusion",
  THREE_PHASE: "threePhase",
  REPORT: "report",
  HISTORY: "history",
  // CALIBRATION: "calibration",
};

// 도움말 KEY 맵핑 상수
const TIP_KEY_BY_TAB = {
  // [TAB_KEYS.ULTRASOUND]: "IMAGE_FILE_ULTRASOUND",
  // [TAB_KEYS.THERMAL]: "IMAGE_FILE_THERMAL",
  // [TAB_KEYS.ULTRAVIOLET]: "IMAGE_FILE_ULTRAVIOLET",
  [TAB_KEYS.FUSION]: "IMAGE_FILE_FUSION",
  [TAB_KEYS.THREE_PHASE]: "IMAGE_FILE_THERMAL",
  [TAB_KEYS.REPORT]: "IMAGE_FILE_REPORT",
};

// HELPER: 탭 목록과 헤더 표시가 항상 같은 라벨을 쓰도록 매핑을 만든다.
const getTabLabelByKey = (t) => ({
  // [TAB_KEYS.ULTRASOUND]: t("imageFileIndex.tabs.ultrasound"),
  // [TAB_KEYS.THERMAL]: t("imageFileIndex.tabs.thermal"),
  // [TAB_KEYS.ULTRAVIOLET]: t("imageFileIndex.tabs.ultraviolet"),
  [TAB_KEYS.FUSION]: t("imageFileIndex.tabs.fusion"),
  [TAB_KEYS.THREE_PHASE]: t("imageFileIndex.tabs.threePhase"),
  [TAB_KEYS.REPORT]: t("imageFileIndex.tabs.report"),
  [TAB_KEYS.HISTORY]: t("fileHistory.tab", { defaultValue: "이력" }),
  // [TAB_KEYS.CALIBRATION]: t("imageFileIndex.tabs.calibration"),
});

// HELPER: 사용자 권한에 따라 보여줄 탭 순서를 정한다.
const getTabLabels = ({ t }) => {
  const tabLabelByKey = getTabLabelByKey(t);
  const tabs = [
    TAB_KEYS.FUSION,
    TAB_KEYS.THREE_PHASE,
    TAB_KEYS.REPORT,
    TAB_KEYS.HISTORY,
  ];

  return tabs.map((value) => ({
    value,
    label: tabLabelByKey[value],
  }));
};

// ───────────────────────────────────────────────
// 📦 Main Component
// ───────────────────────────────────────────────
export default function ImageFilePage() {
  // ────────────────
  // 🧩 상태 & 스토어
  // ────────────────

  const { t, i18n } = useTranslation();
  const user = useAppStore((s) => s.user || null);
  const showAlert = useAppStore((s) => s.showAlert);
  const { project_id, upload_id } = useQueryParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const detailFilterKey = JSON.stringify(parseFileDetailFiltersFromSearchParams(searchParams));
  const detailFilters = useMemo(
    () => JSON.parse(detailFilterKey),
    [detailFilterKey],
  );

  const [file, setFile] = useState(null); // 서버 원본 데이터
  const [prevData, setPrevData] = useState(null); // 편집용 임시 데이터
  const currentTab = resolveFileDetailTab(searchParams.get("tab"), "image");
  const setCurrentTab = (tab) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("tab", resolveFileDetailTab(tab, "image"));
      return next;
    }, { replace: true });
  };
  const [isLoding, setIsLoding] = useState(false); // 로딩 상태
  const [isImageDownloadModalOpen, setIsImageDownloadModalOpen] =
    useState(false);
  const [imageDownloadSelectionMap, setImageDownloadSelectionMap] = useState(
    {},
  );

  const [prevFile, setPrevFile] = useState(null); // 이전 파일 데이터
  const [nextFile, setNextFile] = useState(null); // 다음 파일 데이터

  // 편집모드 (false=보기, true=편집)
  const [isReadOnly, setIsReadOnly] = useState(false);

  // 열화상 관련 상태
  const [circles, setCircles] = useState([undefined, undefined, undefined]);
  const [currentPaletteOptionNumber, setCurrentPaletteOptionNumber] =
    useState();
  const [originPaletteOptionNumber, setOriginPaletteOptionNumber] = useState();
  const [currentAreaCount, setCurrentAreaCount] = useState(0);
  const [currentThermalImg, setCurrentThermalImg] = useState(null);
  const [imgChangeLoading, setImgChangeLoading] = useState(false);
  const threePhaseMarkupCaptureRef = useRef(null);
  const latestUploadIdRef = useRef(String(upload_id ?? ""));
  const fetchSequenceRef = useRef(0);

  // 열화상 캡쳐 대상
  const themralCaptureTargetDefault = useRef(null);
  const tripleSpotCaptureTarget = useRef(null);

  const tabLabels = getTabLabels({ t });

  const currentUploadId = String(upload_id ?? "");
  const fileUploadId = String(file?.upload_id ?? "");
  const isActiveFile = Boolean(file) && fileUploadId === currentUploadId;

  const parseStoredArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value !== "string") return [];

    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const toFiniteNumber = (value) => {
    if (value === null || value === undefined) return null;
    const numericValue = Number(String(value).replace(/[^\d.-]/g, ""));
    return Number.isFinite(numericValue) ? numericValue : null;
  };

  const normalizePointForCompare = (point) => {
    if (!point || typeof point !== "object") return null;

    const x = toFiniteNumber(point.x);
    const y = toFiniteNumber(point.y);
    const value = toFiniteNumber(
      point.value ?? point.temperature ?? point.temp ?? null,
    );

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(value)) {
      return null;
    }

    return {
      x: Number(x.toFixed(3)),
      y: Number(y.toFixed(3)),
      value: Number(value.toFixed(3)),
    };
  };

  const normalizePointForPayload = (point) => {
    if (!point || typeof point !== "object") return null;

    const x = toFiniteNumber(point.x);
    const y = toFiniteNumber(point.y);
    const value = toFiniteNumber(
      point.value ?? point.temperature ?? point.temp ?? null,
    );

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(value)) {
      return null;
    }

    return {
      x: Number(x.toFixed(3)),
      y: Number(y.toFixed(3)),
      value: value.toFixed(1),
    };
  };

  const parseArrayLength = (value) => parseStoredArray(value).length;

  const originalPointList = parseStoredArray(file?.click_points)
    .map(normalizePointForCompare)
    .filter(Boolean);
  const currentPointList = parseStoredArray(circles)
    .map(normalizePointForCompare)
    .filter(Boolean);
  const originalPointCount = originalPointList.length;
  const currentPointCount = currentPointList.length;
  const originalAreaCount = parseArrayLength(file?.click_areas);
  const hasPaletteChanged =
    originPaletteOptionNumber !== currentPaletteOptionNumber;
  const hasPointCountChanged = currentPointCount !== originalPointCount;
  const hasAreaCountChanged = currentAreaCount !== originalAreaCount;
  const hasPointDetailsChanged =
    hasPointCountChanged ||
    originalPointList.some((point, index) => {
      const currentPoint = currentPointList[index];
      if (!currentPoint) return true;

      return (
        point.x !== currentPoint.x ||
        point.y !== currentPoint.y ||
        point.value !== currentPoint.value
      );
    });
  const hasAnyPoint = originalPointCount > 0 || currentPointCount > 0;
  const hasAnyArea = originalAreaCount > 0 || currentAreaCount > 0;
  const shouldRenderThermalSaveButton =
    hasPaletteChanged ||
    hasAnyPoint ||
    hasAnyArea ||
    hasPointDetailsChanged ||
    hasAreaCountChanged;
  const thermalSaveButtonLabel = (i18n.resolvedLanguage || i18n.language || "")
    .toLowerCase()
    .startsWith("ko")
    ? "변경 사항 저장"
    : t("imageFileIndex.buttons.thermalSave");
  const imageDownloadItems = getDownloadableImageItems(file, t);

  const openImageDownloadModal = () => {
    setImageDownloadSelectionMap(
      imageDownloadItems.reduce((acc, item) => {
        acc[item.key] = false;
        return acc;
      }, {}),
    );
    setIsImageDownloadModalOpen(true);
  };

  const toggleImageDownloadSelection = (key) => {
    setImageDownloadSelectionMap((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const selectAllImageDownloads = () => {
    setImageDownloadSelectionMap(
      imageDownloadItems.reduce((acc, item) => {
        acc[item.key] = true;
        return acc;
      }, {}),
    );
  };

  const clearAllImageDownloads = () => {
    setImageDownloadSelectionMap(
      imageDownloadItems.reduce((acc, item) => {
        acc[item.key] = false;
        return acc;
      }, {}),
    );
  };

  const handleDownloadSelectedImages = async () => {
    const selectedItems = imageDownloadItems.filter(
      (item) => imageDownloadSelectionMap[item.key],
    );

    if (!selectedItems.length) {
      showAlert({
        success: false,
        autoClose: true,
        text: t("fusionPanel.status.selectImageRequired", {
          defaultValue: "다운로드할 이미지를 선택해 주세요.",
        }),
      });
      return;
    }

    const baseName = sanitizeFileName(
      file?.clean_file_name || file?.file_name || file?.upload_id || "image",
    );
    let successCount = 0;
    let failCount = 0;

    for (const item of selectedItems) {
      if (!item.src || String(item.src).trim() === "") {
        failCount += 1;
        continue;
      }

      const extension = inferFileExtension(item.src);
      const fileName = `${baseName}_${item.downloadFileKey ?? item.key}.${extension}`;
      const success = await downloadImageFromSource(item.src, fileName);

      if (success) {
        successCount += 1;
      } else {
        failCount += 1;
      }
    }

    if (successCount > 0) {
      showAlert({
        success: true,
        autoClose: true,
        text: t("fusionPanel.status.downloadSuccess", {
          defaultValue: "{{count}}개 이미지를 다운로드했습니다.",
          count: successCount,
        }),
      });
    }

    if (failCount > 0) {
      showAlert({
        success: false,
        autoClose: true,
        text: t("fusionPanel.status.downloadFailed", {
          defaultValue: "{{count}}개 이미지 다운로드에 실패했습니다.",
          count: failCount,
        }),
      });
    }

    setIsImageDownloadModalOpen(false);
  };

  // ───────────────────────────────────────────────
  // 📌 파일 상세 조회 (getFile)
  // ───────────────────────────────────────────────
  // HELPER: 파일 상세를 불러오고, 이전 라우트에서 늦게 도착한 응답은 무시한다.
  const fetchGetFile = useCallback(
    async (targetUploadId = upload_id) => {
      if (!project_id || !targetUploadId) return;

      const requestedUploadId = String(targetUploadId ?? "");
      const requestSeq = ++fetchSequenceRef.current;
      const isStaleRequest = () =>
        latestUploadIdRef.current !== requestedUploadId ||
        fetchSequenceRef.current !== requestSeq;

      const { sortKey, sortDir } = getFileSortPref(user?.id);
      const locale = i18n.resolvedLanguage || i18n.language || "ko-KR";

      try {
        const {
          success,
          message,
          messageKey,
          messageParams,
          data,
          prevFile,
          nextFile,
        } = await getFile(
          project_id,
          targetUploadId,
          user?.id,
          sortKey,
          sortDir,
          locale,
          detailFilters,
        );
        if (isStaleRequest()) return;

        if (success) {
          setFile(data);

          setPrevFile(prevFile);
          setNextFile(nextFile);
          setOriginPaletteOptionNumber(data.thermal_palette);
          setCircles([
            data?.click_points?.[0] ?? undefined,
            data?.click_points?.[1] ?? undefined,
            data?.click_points?.[2] ?? undefined,
          ]);
        } else {
          showAlert({
            text: message,
            messageKey,
            messageParams,
            success: false,
            autoClose: true,
          });
          navigate(-1);
        }
      } catch (err) {
        if (isStaleRequest()) return;

        console.error(err);
        showAlert({
          text: "파일 조회 중 오류가 발생했습니다.",
          success: false,
          autoClose: true,
        });
        navigate(-1);
      }
    },
    [
      project_id,
      upload_id,
      user?.id,
      navigate,
      showAlert,
      i18n.language,
      i18n.resolvedLanguage,
      detailFilters,
    ],
  );

  // ───────────────────────────────────────────────
  // 🌀 최초 마운트 시 파일 로드
  // ───────────────────────────────────────────────
  // STEP 1: 라우트의 upload id가 바뀌면 파일 단위 UI 상태를 먼저 초기화한다.
  useEffect(() => {
    latestUploadIdRef.current = currentUploadId;
    setIsLoding(true);
    setFile(null);
    setPrevData(null);
    setPrevFile(null);
    setNextFile(null);
    setIsReadOnly(false);
    setCurrentThermalImg(null);
    setImgChangeLoading(false);
    setIsImageDownloadModalOpen(false);
    setImageDownloadSelectionMap({});
    setCurrentPaletteOptionNumber(undefined);
    setOriginPaletteOptionNumber(undefined);
    setCircles([undefined, undefined, undefined]);
    return () => {
    };
  }, [currentUploadId]);

  // STEP 2: 새 파일을 조회하고, 현재 활성 upload id일 때만 로딩을 해제한다.
  useEffect(() => {
    const requestedUploadId = currentUploadId;
    let cancelled = false;

    (async () => {
      await fetchGetFile(requestedUploadId);
      if (!cancelled && latestUploadIdRef.current === requestedUploadId) {
        setIsLoding(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUploadId, fetchGetFile]);

  // ───────────────────────────────────────────────
  // 🧾 보고서 Props 구성
  // ───────────────────────────────────────────────
  const ReportProps = {
    form: file,
    setForm: setFile,
    prevData,
    setPrevData,
    isReadOnly,
    setIsReadOnly,
    type: "file",
    user_id: user?.id,
    project_id,
    refetch: fetchGetFile, // 저장 후 서버 재조회용
  };

  // ───────────────────────────────────────────────
  // 💾 열화상 정보 저장 (이미지 + 팔레트 + 점 좌표)
  // ───────────────────────────────────────────────
  const handleUpdateThermalInfo = async (saveOptions = {}) => {
    if (imgChangeLoading) return;

    setImgChangeLoading(true);
    const locale = i18n.resolvedLanguage || i18n.language || "ko-KR";
    const requestedCreateInspectionResult =
      saveOptions &&
      typeof saveOptions === "object" &&
      saveOptions.createInspectionResult === true;

    let clickAreasPayload = null;
    let markupTripleSpotDataUrl = null;
    const clickPointsPayload = parseStoredArray(circles)
      .map(normalizePointForPayload)
      .filter(Boolean);
    const createInspectionResultFlag =
      currentTab === TAB_KEYS.THREE_PHASE && clickPointsPayload.length >= 3
        ? requestedCreateInspectionResult
        : undefined;

    if (currentTab === TAB_KEYS.THREE_PHASE) {
      try {
        const threePhaseCapture =
          (await threePhaseMarkupCaptureRef.current?.()) ?? null;
        markupTripleSpotDataUrl =
          typeof threePhaseCapture === "string"
            ? threePhaseCapture
            : (threePhaseCapture?.dataUrl ?? null);
        clickAreasPayload = Array.isArray(threePhaseCapture?.clickAreas)
          ? threePhaseCapture.clickAreas
          : null;
      } catch (error) {
        console.error("three phase markup capture failed:", error);
      }
    }

    const { success, message, messageKey, messageParams } =
      await postUpdateThermalInfo({
        refs: [themralCaptureTargetDefault, tripleSpotCaptureTarget],
        markupTripleSpotDataUrl,
        project_id,
        upload_id,
        click_points: clickPointsPayload,
        click_areas: clickAreasPayload,
        create_inspection_result: createInspectionResultFlag,
        currentPaletteOptionNumber:
          currentPaletteOptionNumber ?? originPaletteOptionNumber,
        volume: file?.volume,
        avg_decibel: file?.avg_decibel,
        inspection_result: file?.inspection_result,
        fusion_result: file?.fusion_result,
        locale,
      });

    if (success) {
      await fetchGetFile();
    }

    setImgChangeLoading(false);

    showAlert({
      text: message,
      messageKey,
      messageParams,
      success: success,
      autoClose: true,
    });

    setCurrentThermalImg(null);
  };



  // ───────────────────────────────────────────────
  // ⏳ 로딩 상태
  // ───────────────────────────────────────────────
  if (!file || !isActiveFile || isLoding) {
    return (
      <section className="fileDetail content">
        <Spinner />
      </section>
    );
  }

  // ───────────────────────────────────────────────
  // 🧭 렌더링
  // ───────────────────────────────────────────────
  return (
    <section className="fileDetail content">
      {/* ───────────── 헤더 영역 ───────────── */}
      <div className="fileDetailHeader">
        <div className="fileDetailHeader__main">
          <GoBackButton t={t} index="temp" />
          <div className="fileDetailHeader__identity">
            <FileTreePath file={file} uploadId={upload_id} />
            <div className="editorFormTitleBox helpTitleRow">
              <h2 title={file?.file_name} tabIndex={0}>{file?.file_name}</h2>
            </div>
          </div>
          <PrevNextNavToFIleIndex
            prevFile={prevFile}
            nextFile={nextFile}
            project_id={project_id}
            upload_id={upload_id}
            currentTab={currentTab}
          />
        </div>
        {/* ───────────── 탭 영역 ───────────── */}
        <div className="fileDetail__toolbar">
          <div className="fileDetailHeader__tabs">
            <TabMenu
              labels={tabLabels}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
            />
            {TIP_KEY_BY_TAB[currentTab] && (
              <HelpTip
                tipKey={TIP_KEY_BY_TAB[currentTab]}
                className="fileDetailHeader__help"
                placement="bottom"
                maxWidth={360}
              />
            )}
          </div>
          <div className="fileDetailHeader__actions">
            <DownloadUdFile t={t} streamUrl={file?.ud_path} />
            <button
              type="button"
              onClick={openImageDownloadModal}
              className="btn-bg-blue"
            >
              {t("imageFileIndex.buttons.singleImageDownload", {
                defaultValue: "이미지 다운로드",
              })}
            </button>
            {/* 열화상 수정 감지 → 저장 버튼 표시 */}
            {currentTab === TAB_KEYS.THERMAL && shouldRenderThermalSaveButton && (
              <button onClick={handleUpdateThermalInfo} className="btn-bg-blue fileDetailHeader__primaryAction">
                {t("imageFileIndex.buttons.thermalSave")}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* {currentTab === TAB_KEYS.ULTRASOUND && (
        <UltrasonicPanel key={`ultrasonic-${fileUploadId}`} t={t} file={file} />
      )} */}
      {/* {currentTab === TAB_KEYS.THERMAL && (
        <ThermalPanel
          key={`thermal-${fileUploadId}`}
          file={file}
          t={t}
          refetch={fetchGetFile}
          circles={circles}
          setCircles={setCircles}
          currentPaletteOptionNumber={currentPaletteOptionNumber}
          setCurrentPaletteOptionNumber={setCurrentPaletteOptionNumber}
          themralCaptureTargetDefault={themralCaptureTargetDefault}
          tripleSpotCaptureTarget={tripleSpotCaptureTarget}
          currentThermalImg={currentThermalImg}
          setCurrentThermalImg={setCurrentThermalImg}
          imgChangeLoading={imgChangeLoading}
          setImgChangeLoading={setImgChangeLoading}
        />
      )} */}
      {currentTab === TAB_KEYS.THREE_PHASE && (
        <ThreePhasePanel
          key={`three-phase-${fileUploadId}`}
          file={file}
          t={t}
          circles={circles}
          setCircles={setCircles}
          currentPaletteOptionNumber={currentPaletteOptionNumber}
          setCurrentPaletteOptionNumber={setCurrentPaletteOptionNumber}
          tripleSpotCaptureTarget={tripleSpotCaptureTarget}
          currentThermalImg={currentThermalImg}
          setCurrentThermalImg={setCurrentThermalImg}
          imgChangeLoading={imgChangeLoading}
          setImgChangeLoading={setImgChangeLoading}
          markupCaptureRef={threePhaseMarkupCaptureRef}
          onAreaCountChange={setCurrentAreaCount}
          showSaveThermalButton={shouldRenderThermalSaveButton}
          onSaveThermalInfo={handleUpdateThermalInfo}
          saveThermalButtonLabel={thermalSaveButtonLabel}
        />
      )}
      {/* {currentTab === TAB_KEYS.ULTRAVIOLET && (
        <UltravioletPanel key={`uv-${fileUploadId}`} t={t} file={file} />
      )} */}
      {currentTab === TAB_KEYS.FUSION && (
        <FusionPanel
          key={`fusion-${fileUploadId}`}
          t={t}
          file={file}
          onMoveToThreePhase={() => setCurrentTab(TAB_KEYS.THREE_PHASE)}
        />
      )}
      {currentTab === TAB_KEYS.REPORT && (
        <ImageReportPanel
          key={`report-${fileUploadId}`}
          t={t}
          editorProps={ReportProps}
          isReadOnly={isReadOnly}
          setIsReadOnly={setIsReadOnly}
          file={file}
          form={prevData}
          setForm={setPrevData}
          setFile={setFile}
          projectId={project_id}
          userId={user?.id}
          locale={i18n.resolvedLanguage || i18n.language || "ko-KR"}
          reportActionSlot={<EditorBtnBox props={ReportProps} />}
        />
      )}
      {currentTab === TAB_KEYS.HISTORY && (
        <FileHistoryPanel key={`history-${fileUploadId}`} file={file} projectId={project_id} userId={user?.id} />
      )}
      {/* {currentTab === TAB_KEYS.CALIBRATION && (
        <ImageCalibrationPanel
          key={`calibration-${fileUploadId}`}
          file={file}
        />
      )} */}
      <FusionDownloadModal
        open={isImageDownloadModalOpen}
        items={imageDownloadItems}
        selectedMap={imageDownloadSelectionMap}
        t={t}
        onClose={() => setIsImageDownloadModalOpen(false)}
        onToggle={toggleImageDownloadSelection}
        onSelectAll={selectAllImageDownloads}
        onClearAll={clearAllImageDownloads}
        onDownload={handleDownloadSelectedImages}
      />

      {/* {!file?.markup_already && (
        <AutoUploadOnRender
          key={`auto-upload-${fileUploadId}`}
          file={file}
          locale={i18n?.language}
          fetchGetFile={fetchGetFile}
        />
      )} */}
    </section>
  );
}
